import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  TableDots,
  Pagination,
  ContainerAvatar,
  enqueueSnackbar,
  Chip,
  offerConfirmModal,
  offerUpdateModal,
  offerCancelModal,
  offerConfirmPaymentModal,
  Checkbox,
  TableIconsCell,
  Button,
  offerPayModal,
  shareImagesModal,
  SimpleTable,
  CheckboxLabel,
  containerListModal,
  offerCancelPaidModal,
} from '../../design-system';
import {
  CardActions,
  Cards,
  CardsActions,
  CardsWrapper,
  CardWrapper,
  DashboardHeader,
  FilterIconButton,
} from '../dashboard';
import { Filters } from './Filters';
import { MobileFilters } from './MobileFilters';
import { Card } from './Cards';
import {
  momentTextFormat,
  showToRoles,
  formatContainerType,
  getOfferOptions,
  showToRolesMobile,
} from '../../utils';
import { getNotFounds } from './utils';
import {
  useSort,
  usePaginationState,
  useOffersQuery,
  useIsSmallScreen,
  smallScreenSize,
  useSaveFilters,
  clearSavedFilters,
} from '../../hooks';
import {
  TABLE,
  MESSAGES,
  OfferStatusToChipColors,
  OfferStatusSentToChipLabels,
  OfferStatusReceivedToChipLabels,
  OfferStatusAdminToChipLabels,
} from '../../const';
import { FeeType, OffersGetAllQuery, OfferStatus } from '../../types';
import { containersService } from '../../services';
import { i18n } from '../../i18n';
import { useStoreUser } from '../../context';

let openFilterPopup: any = () => null;
let openContainerListPopup: any = () => null;
let cancelContainerList: any = () => null;

interface OffersPageProps {
  innerScroll?: boolean;
}

export const OffersPage = styled.div<OffersPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;

      @media (max-width: ${smallScreenSize}px) {
        overflow: initial;
      }
    `}

  @media (max-width: ${smallScreenSize}px) {
    grid-row-gap: var(--spacing-3);

    & > nav > * {
      justify-self: center;
    }
  }
`;

const TotalPayWrapper = styled.div`
  position: absolute;
  right: 2.8rem;
  bottom: 2.8rem;

  @media (max-width: ${smallScreenSize}px) {
    bottom: 0;
    right: 0;
  }
`;

interface OffersHeaderProps {
  totalCount: number;
  data: OffersGetAllQuery['data'];
  containerList: any;
  setContainerList: (containerList?: any) => void;
  checkIndexs: number[];
}

export const OffersHeader = ({
  totalCount,
  data,
  containerList,
  setContainerList,
  checkIndexs,
}: OffersHeaderProps) => {
  const { user } = useStoreUser();
  const isSmallScreen = useIsSmallScreen();

  const totalPrice = data
    .filter((item) => item.status === OfferStatus.ACCEPTED || item.status === OfferStatus.SOLD_INST)
    .reduce((acc, item) => acc + item.price[0], 0);
  const selectedTotalPrice = data
    .filter((_, i) => checkIndexs.includes(i))
    .reduce((prev, curr) => prev + curr.price[0], 0);

  return (
    <DashboardHeader isSmallScreen={isSmallScreen}>
      <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
        {!isSmallScreen && i18n.t('pages.offer.title', { totalCount, totalPrice })}
        {isSmallScreen && i18n.t('pages.offer.shortTitle', { totalCount, totalPrice })}
      </Typography>
      {!!checkIndexs.length && (
        <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
          {!isSmallScreen &&
            i18n.t('pages.offer.selectedTitle', { count: checkIndexs.length, selectedTotalPrice })}
          {isSmallScreen &&
            i18n.t('pages.offer.shortSelectedTitle', {
              count: checkIndexs.length,
              selectedTotalPrice,
            })}
        </Typography>
      )}
      {!isSmallScreen && containerList && (
        <Button
          size={'medium'}
          onClick={() => {
            cancelContainerList();
            setContainerList();
          }}
          data-color={'white'}
        >
          {i18n.t('button.cancel')}
        </Button>
      )}
      {!isSmallScreen && !containerList && (
        <Button size={'medium'} onClick={openContainerListPopup}>
          {i18n.t('button.containerList')}
        </Button>
      )}
      {showToRolesMobile('client', user, isSmallScreen) && (
        <FilterIconButton onClick={openFilterPopup} />
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  user: any;
  data: OffersGetAllQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  user,
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const handleClickAction = (val: any) => {
    if (val === 'mark-as-paid' || val === 'mark-as-unpaid' || val === 'mark-as-installment') {
      const filterMarkAsPaid = (item: any) =>
        showToRoles('admin', user) &&
        (item.status === OfferStatus.ACCEPTED ||
          (showToRoles(['SUPER_ADMIN'], user) && item.status === OfferStatus.SOLD_INST));
      const filterOtherAction = (item: any) =>
        (showToRoles(['SUPER_ADMIN'], user) ||
          (showToRoles('admin', user) && val !== 'mark-as-installment')) &&
        item.status === OfferStatus.ACCEPTED;

      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter(val === 'mark-as-paid' ? filterMarkAsPaid : filterOtherAction);

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      offerConfirmPaymentModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.container.number,
        price: item.price[0],
      }));
      offerConfirmPaymentModal.isPaid = val === 'mark-as-paid';
      offerConfirmPaymentModal.isInstallment = val === 'mark-as-installment';

      const messages = {
        'mark-as-paid': MESSAGES.OFFER.SUCCESS.MARK_AS_PAID,
        'mark-as-unpaid': MESSAGES.OFFER.SUCCESS.MARK_AS_UNPAID,
        'mark-as-installment': MESSAGES.OFFER.SUCCESS.MARK_AS_INSTALLMENT,
      };

      offerConfirmPaymentModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(messages[val as keyof typeof messages], 'success');
        refetch();
      };

      offerConfirmPaymentModal.open();
    }
    if (val === 'cancel') {
      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter((item) => showToRoles('client', user) && item.status === OfferStatus.ACCEPTED);

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      offerCancelModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.container.number,
        price: item.price[0],
      }));

      offerCancelModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CART.SUCCESS.REMOVE, 'success');
        refetch();
      };
      offerCancelModal.open();
    }
    if (val === 'pay') {
      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter(
          (item) =>
            showToRoles('client', user) &&
            (item.status === OfferStatus.ACCEPTED || item.status === OfferStatus.SOLD_INST),
        );

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      offerPayModal.data = validatedData.map((item) => ({
        price: item.price[0],
      }));

      offerPayModal.submitClick = () => {
        setCheckIndexs([]);
      };
      offerPayModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  if (showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user)) {
    return (
      <>
        {data
          .filter((_: any, i: number) => checkIndexs.includes(i))
          .some(
            (item) =>
              item.status === OfferStatus.ACCEPTED ||
              (item.status === OfferStatus.SOLD_INST && showToRoles(['SUPER_ADMIN'], user)),
          ) && (
          <Button
            size={'medium'}
            data-color={'white'}
            onClick={() => handleClickAction('mark-as-paid')}
          >
            {i18n.t('dropdown.markAsPaid')}
          </Button>
        )}
        {data
          .filter((_: any, i: number) => checkIndexs.includes(i))
          .some((item) => item.status === OfferStatus.ACCEPTED) && (
          <>
            <Button
              size={'medium'}
              data-color={'white'}
              onClick={() => handleClickAction('mark-as-unpaid')}
            >
              {i18n.t('dropdown.markAsUnpaid')}
            </Button>
            {showToRoles(['SUPER_ADMIN'], user) && (
              <Button
                size={'medium'}
                data-color={'white'}
                onClick={() => handleClickAction('mark-as-installment')}
              >
                {i18n.t('dropdown.markAsInstallment')}
              </Button>
            )}
          </>
        )}
      </>
    );
  }

  if (showToRoles('client', user)) {
    return (
      <>
        {data
          .filter((_: any, i: number) => checkIndexs.includes(i))
          .some((item) => item.status === OfferStatus.ACCEPTED) && (
          <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('cancel')}>
            {i18n.t('button.cancel')}
          </Button>
        )}
        <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('pay')}>
          {i18n.t('dropdown.pay')}
        </Button>
      </>
    );
  }

  return <></>;
};

export const OffersBody = ({
  user,
  states,
  setStates,
  filteredStates,
  setData,
  setTotalCount,
  containerList,
  setContainerList,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  handleClickImage,
}: any) => {
  const isSmallScreen = useIsSmallScreen();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [isOpenFilter, setIsOpenFilter] = useState(false);

  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'createdAt',
    sortDirection: 'desc',
  });
  const [filterKey, setFilterKey] = useSaveFilters('filters-key', true, Math.random());

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useOffersQuery({
    ...filteredStates,
    take,
    skip,
    sortField,
    sortDirection,
  });

  const filteredClientCheckIndex =
    showToRoles('client', user) &&
    checkIndexs.filter(
      (i: number) =>
        data.data[i].status === OfferStatus.ACCEPTED ||
        data.data[i].status === OfferStatus.SOLD_INST,
    );

  useEffect(() => {
    let oldContainerList = containerList;
    if (isFetching) return;

    if (!oldContainerList && typeof filteredStates.number !== 'string') {
      oldContainerList = filteredStates.number;
      setContainerList(oldContainerList);
    }
    setTotalCount(
      oldContainerList ? getNotFounds(data, oldContainerList).totalCount : data.totalCount,
    );
    setData((oldContainerList ? getNotFounds(data, oldContainerList) : data).data);
  }, [isFetching]);

  useEffect(() => {
    openFilterPopup = () => setIsOpenFilter(true);
    openContainerListPopup = () => {
      const fc = (fcProps = { containerNumber: [] }) => {
        const containerList = fcProps.containerNumber;
        if (!containerList.length) return;

        clearSavedFilters();
        setContainerList(containerList);
        setFilterKey(Math.random());
        setCheckIndexs([]);
        setStates('cancelall', {
          status: 'All',
          number: containerList,
        });
      };
      containerListModal.fc = fc;
      containerListModal.isFilterList = true;
      containerListModal.open();
    };
    cancelContainerList = () => {
      clearSavedFilters();
      setFilterKey(Math.random());
      setCheckIndexs([]);
      setStates('cancelall');
    };
  }, []);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args: any) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (row: any, number: any, val: any, id: any) => {
    if (val === 'mark-as-paid' || val === 'mark-as-unpaid' || val === 'mark-as-installment') {
      offerConfirmPaymentModal.data = [{ id, number, price: row.price[0] }];
      offerConfirmPaymentModal.isPaid = val === 'mark-as-paid';
      offerConfirmPaymentModal.isInstallment = val === 'mark-as-installment';

      const messages = {
        'mark-as-paid': MESSAGES.OFFER.SUCCESS.MARK_AS_PAID,
        'mark-as-unpaid': MESSAGES.OFFER.SUCCESS.MARK_AS_UNPAID,
        'mark-as-installment': MESSAGES.OFFER.SUCCESS.MARK_AS_INSTALLMENT,
      };

      offerConfirmPaymentModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(messages[val as keyof typeof messages], 'success');
        refetch();
      };
      offerConfirmPaymentModal.open();
    }
    if (val === 'accept-offer' || val === 'reject-offer') {
      offerConfirmModal.id = id;
      offerConfirmModal.number = number;
      offerConfirmModal.price = row.price[0];
      offerConfirmModal.isAccept = val === 'accept-offer';
      offerConfirmModal.submitClick = () => {
        setCheckIndexs([]);
        const message =
          val === 'accept-offer' ? MESSAGES.OFFER.SUCCESS.ACCEPT : MESSAGES.OFFER.SUCCESS.REJECT;
        enqueueSnackbar(message, 'success');
        refetch();
      };
      offerConfirmModal.open();
    }
    if (val === 'update') {
      offerUpdateModal.id = id;
      offerUpdateModal.number = number;
      offerUpdateModal.price = row.price[0];
      offerUpdateModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.OFFER.SUCCESS.UPDATE, 'success');
        refetch();
      };
      offerUpdateModal.open();
    }
    if (val === 'cancel') {
      offerCancelModal.data = [{ id, number, price: row.price[0] }];
      offerCancelModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CART.SUCCESS.REMOVE, 'success');
        refetch();
      };
      offerCancelModal.open();
    }
    if (val === 'cancel-paid') {
      offerCancelPaidModal.data = [{ id, number, price: row.price[0] }];
      offerCancelPaidModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.OFFER.SUCCESS.CANCEL_PAID, 'success');
        refetch();
      };
      offerCancelPaidModal.open();
    }
    if (val === 'pay' || val === 'pay-total') {
      if (val === 'pay-total') {
        const validatedData = data.data
          .filter((_: any, i: number) => checkIndexs.includes(i))
          .filter(
            (item) =>
              showToRoles('client', user) &&
              (item.status === OfferStatus.ACCEPTED || item.status === OfferStatus.SOLD_INST),
          );

        if (!validatedData.length) {
          return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
        }

        offerPayModal.data = validatedData.map((item) => ({
          price: item.price[0],
        }));
      } else {
        offerPayModal.data = [{ price: row.price[0] }];
      }

      offerPayModal.submitClick = () => {
        setCheckIndexs([]);
      };
      offerPayModal.open();
    }
    if (val === 'download-images') {
      containersService.downloadImages(row.container.images, number);
    }
    if (val === 'share-images') {
      shareImagesModal.images = row.container.images;
      shareImagesModal.containerNumber = number;
      shareImagesModal.open();
    }
  };

  if (showToRolesMobile('client', user, isSmallScreen))
    return (
      <>
        <CardsWrapper hasActions={showToRoles('client', user)}>
          <CardsActions>
            <CheckboxLabel
              indeterminate={!!checkIndexs.length && checkIndexs.length !== data.data.length}
              checked={checkIndexs.length === data.data.length}
              onChange={() => handleCheckboxChange()}
              onClick={(e) => e.stopPropagation()}
              label={!checkIndexs.length ? i18n.t('label.all') : ''}
            />
            <CheckboxActions
              user={user}
              checkIndexs={checkIndexs.filter(
                (i: number) =>
                  data.data[i].status === OfferStatus.ACCEPTED ||
                  data.data[i].status === OfferStatus.SOLD_INST,
              )}
              data={data.data}
              refetch={refetch}
              setCheckIndexs={setCheckIndexs}
            />
          </CardsActions>
          <Cards>
            {data.data.map((offer, i) => (
              <CardWrapper key={i}>
                <CardActions
                  isCenter={
                    !showToRoles(
                      [
                        'SUPER_ADMIN',
                        'YARD_COMPANY_ADMIN',
                        'YARD_MANAGER',
                        'CLIENT_MANAGER',
                        'CLIENT_DISPATCHER',
                      ],
                      user,
                    )
                  }
                >
                  {!!getOfferOptions(offer, user, states.offerDirection).length && (
                    <TableDots
                      open={openDotsMenuIndex === i}
                      onToggle={() => handleDotsToggle(i)}
                      options={getOfferOptions(offer, user, states.offerDirection)}
                      onSelect={(val) => {
                        handleDotsOptionSelect(offer, offer.container.number, val, offer.id);
                      }}
                    />
                  )}
                  {showToRoles(
                    [
                      'SUPER_ADMIN',
                      'YARD_COMPANY_ADMIN',
                      'YARD_MANAGER',
                      'CLIENT_MANAGER',
                      'CLIENT_DISPATCHER',
                    ],
                    user,
                  ) && (
                    <Checkbox
                      checked={checkIndexs.includes(i)}
                      onChange={() => handleCheckboxChange(i)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </CardActions>
                <Card
                  states={states}
                  handleClickImage={handleClickImage}
                  handleDotsOptionSelect={handleDotsOptionSelect}
                  {...offer}
                />
              </CardWrapper>
            ))}
          </Cards>
        </CardsWrapper>
        <Pagination
          count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
          siblingCount={0}
          page={page}
          onChange={handleOnPageChange}
        />
        {!!filteredClientCheckIndex.length && (
          <TotalPayWrapper>
            <Button
              size={'medium'}
              onClick={() => handleDotsOptionSelect(null, null, 'pay-total', null)}
            >
              {i18n.t('pages.offer.payButton', {
                total: filteredClientCheckIndex.reduce(
                  (acc: number, i: number) => acc + data.data[i].price[0],
                  0,
                ),
              })}
            </Button>
          </TotalPayWrapper>
        )}
        <MobileFilters
          isOpen={isOpenFilter}
          setIsOpen={setIsOpenFilter}
          setStates={handleFilterChange}
          user={user}
          offerDirection={states.offerDirection}
          states={states}
          {...filteredStates}
        />
      </>
    );

  return (
    <SimpleTable
      data={containerList ? getNotFounds(data, containerList) : data}
      filters={() => (
        <Filters key={filterKey} user={user} setStates={handleFilterChange} {...states} />
      )}
      checkboxActions={(data) => (
        <CheckboxActions
          user={user}
          checkIndexs={checkIndexs.filter(
            (i: number) =>
              data[i].status === OfferStatus.ACCEPTED || data[i].status === OfferStatus.SOLD_INST,
          )}
          data={data}
          refetch={refetch}
          setCheckIndexs={setCheckIndexs}
        />
      )}
      tableRowProps={(offer, i) => ({
        key: offer.container.number + i,
        onClick: () => {},
      })}
      columns={[
        {
          column: 1,
          head: () => (
            <TableHeadCell align={'left'} withGap>
              {showToRoles(
                [
                  'SUPER_ADMIN',
                  'YARD_COMPANY_ADMIN',
                  'YARD_MANAGER',
                  'CLIENT_MANAGER',
                  'CLIENT_DISPATCHER',
                ],
                user,
              ) && (
                <Checkbox
                  indeterminate={
                    !!checkIndexs.length &&
                    checkIndexs.length !== getNotFounds(data, containerList).data.length
                  }
                  checked={checkIndexs.length === getNotFounds(data, containerList).data.length}
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {i18n.t('label.actions')}
            </TableHeadCell>
          ),
          body: (offer, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {showToRoles(
                [
                  'SUPER_ADMIN',
                  'YARD_COMPANY_ADMIN',
                  'YARD_MANAGER',
                  'CLIENT_MANAGER',
                  'CLIENT_DISPATCHER',
                ],
                user,
              ) && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {!!getOfferOptions(offer, user, states.offerDirection).length && (
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={getOfferOptions(offer, user, states.offerDirection)}
                  onSelect={(val) => {
                    handleDotsOptionSelect(offer, offer.container.number, val, offer.id);
                  }}
                />
              )}
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('label.photo'),
          body: (offer) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar
                images={offer.container.images}
                onClick={() => handleClickImage(offer.container.images)}
              />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (offer) => offer.container.number,
        },
        {
          headText: () => i18n.t('label.type'),
          bodyText: (offer) => formatContainerType(offer.container.type),
        },
        {
          headText: () => i18n.t('label.status'),
          body: (offer) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={
                  (showToRoles('adminRead', user)
                    ? OfferStatusAdminToChipLabels
                    : states.offerDirection === 'sent'
                    ? OfferStatusSentToChipLabels
                    : OfferStatusReceivedToChipLabels)[offer.status]
                }
                color={OfferStatusToChipColors[offer.status]}
              />
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.buyerClient'),
          bodyText: (offer) => offer.buyerClient.code,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.sellerClient'),
          bodyText: (offer) => offer.sellerClient.code,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.price'),
          bodyText: (offer) =>
            `${
              showToRoles('adminRead', user) || states.offerDirection === 'received'
                ? offer.ownerPrice[0]
                : offer.price[0]
            }$`,
        },
        {
          headText: () => i18n.t('label.fee'),
          bodyText: (offer) =>
            `${offer.fee[0]?.value}${offer.fee[0]?.type === FeeType.FIXED ? '$' : '%'}`,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.finalPrice'),
          bodyText: (offer) => `${offer.price[0]}$`,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'createdAt'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.createdAt')}
            </TableHeadCell>
          ),
          bodyText: (offer) => (offer.createdAt ? momentTextFormat(offer.createdAt, user) : ''),
        },
        {
          headText: () => i18n.t('label.paidAt'),
          bodyText: (offer) => (offer.closedAt ? momentTextFormat(offer.closedAt, user) : ''),
          isHidden: () => !(states.status === 'CLOSED' || states.status === 'All'),
        },
      ]}
      footer={
        containerList
          ? () => (
              <>
                {!!filteredClientCheckIndex.length && (
                  <TotalPayWrapper>
                    <Button
                      size={'medium'}
                      onClick={() => handleDotsOptionSelect(null, null, 'pay-total', null)}
                    >
                      {i18n.t('pages.offer.payButton', {
                        total: filteredClientCheckIndex.reduce(
                          (acc: number, i: number) => acc + data.data[i].price[0],
                          0,
                        ),
                      })}
                    </Button>
                  </TotalPayWrapper>
                )}
              </>
            )
          : () => (
              <TableWrapperFooter>
                <Pagination
                  count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
                  page={page}
                  onChange={handleOnPageChange}
                />
                {!!filteredClientCheckIndex.length && (
                  <TotalPayWrapper>
                    <Button
                      size={'medium'}
                      onClick={() => handleDotsOptionSelect(null, null, 'pay-total', null)}
                    >
                      {i18n.t('pages.offer.payButton', {
                        total: filteredClientCheckIndex.reduce(
                          (acc: number, i: number) => acc + data.data[i].price[0],
                          0,
                        ),
                      })}
                    </Button>
                  </TotalPayWrapper>
                )}
              </TableWrapperFooter>
            )
      }
    />
  );
};
